import React from 'react';

/**
 * V2 compound component slot marker.
 *
 * Each slot component (SearchBar, Filter, Pagination, ActionButton, ...) is
 * tagged with this property so orchestrators (`Toolbar`, `Root`) can locate
 * it inside a children tree without imposing a rigid JSX order.
 */
export const PLT_SLOT = '__pltV2Slot' as const;

export type PltSlotName =
  | 'header'
  | 'toolbar'
  | 'search'
  | 'filter'
  | 'pagination'
  | 'action'
  | 'list'
  | 'extraHeader';

export function markSlot<T>(Component: T, slot: PltSlotName): T {
  (Component as unknown as Record<string, unknown>)[PLT_SLOT] = slot;
  return Component;
}

function getSlotName(node: React.ReactNode): PltSlotName | null {
  if (!React.isValidElement(node)) return null;
  const type = node.type as unknown as Record<string, unknown> | null;
  const name = type?.[PLT_SLOT];
  return typeof name === 'string' ? (name as PltSlotName) : null;
}

export function findSlot(
  children: React.ReactNode,
  slot: PltSlotName,
): React.ReactElement | undefined {
  return React.Children.toArray(children).find(
    (c): c is React.ReactElement => getSlotName(c) === slot,
  );
}

export function findNonSlotChildren(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).filter(c => getSlotName(c) == null);
}
